// web/src/components/Sticker.tsx
// Tilted, brightly-coloured badge — replaces the muted "medallion" idea.
// Used for:
//  - MoM (electric yellow)
//  - Captain (royal blue)
//  - Milestones / debuts (accent coral)
//  - Achievement unlocks (rarity-tinted)
//
// Sits on top of a CoverHero or as the unit of the Achievements wall.
// Drops in with a scale + rotate animation when rendered fresh.

import type { ReactNode, CSSProperties } from 'react';
import { motion } from 'framer-motion';

export type StickerTone =
  | 'mom'
  | 'captain'
  | 'milestone'
  | 'achievement-common'
  | 'achievement-rare'
  | 'achievement-epic'
  | 'achievement-legendary'
  | 'neutral';

export interface StickerProps {
  /** Big, all-caps label. Keep it short — 1–3 words. */
  label: string;
  /** Sub-label below the main one (e.g. badge category or date). */
  subLabel?: string;
  tone?: StickerTone;
  /** Tilt in degrees (default −6, set 0 to disable). */
  tilt?: number;
  /** Render the entry animation. Default true. */
  animate?: boolean;
  /** Optional leading icon. */
  icon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  style?: CSSProperties;
}

const TONE_BG: Record<StickerTone, string> = {
  mom:                    'var(--electric-yellow)',
  captain:                'var(--royal)',
  milestone:              'var(--accent-coral)',
  'achievement-common':   'var(--surface-2)',
  'achievement-rare':     'var(--royal)',
  'achievement-epic':     'var(--accent-coral)',
  'achievement-legendary':'var(--electric-yellow)',
  neutral:                'var(--gold)',
};

const TONE_INK: Record<StickerTone, string> = {
  mom:                    '#2a1d0c',
  captain:                'var(--ink-on-green)',
  milestone:              'var(--ink-on-green)',
  'achievement-common':   'var(--green)',
  'achievement-rare':     'var(--ink-on-green)',
  'achievement-epic':     'var(--ink-on-green)',
  'achievement-legendary':'#2a1d0c',
  neutral:                '#2a1d0c',
};

const SIZE: Record<NonNullable<StickerProps['size']>, { pad: string; main: string; sub: string }> = {
  sm: { pad: '6px 12px',  main: '0.875rem', sub: '0.6rem'   },
  md: { pad: '10px 18px', main: '1.125rem', sub: '0.7rem'   },
  lg: { pad: '14px 24px', main: '1.5rem',   sub: '0.75rem'  },
};

export function Sticker({
  label,
  subLabel,
  tone = 'neutral',
  tilt = -6,
  animate = true,
  icon,
  size = 'md',
  style,
}: StickerProps) {
  const s = SIZE[size];
  return (
    <motion.div
      initial={animate ? { opacity: 0, scale: 0.6, rotate: tilt - 12 } : undefined}
      animate={animate ? { opacity: 1, scale: 1, rotate: tilt } : undefined}
      transition={{ duration: 0.6, ease: [0.34, 1.4, 0.64, 1] }}  /* slight overshoot */
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        background: TONE_BG[tone],
        color: TONE_INK[tone],
        padding: s.pad,
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sticker)',
        transform: `rotate(${tilt}deg)`,
        fontFamily: 'var(--font-masthead)',
        textTransform: 'uppercase',
        ...style,
      }}
    >
      {icon ? <span style={{ display: 'inline-flex', fontSize: s.main }}>{icon}</span> : null}
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        <span
          style={{
            fontSize: s.main,
            letterSpacing: '0.04em',
            fontStyle: 'italic',
          }}
        >
          {label}
        </span>
        {subLabel ? (
          <span
            style={{
              marginTop: 2,
              fontFamily: 'var(--font-sans)',
              fontStyle: 'normal',
              fontSize: s.sub,
              letterSpacing: '0.16em',
              opacity: 0.8,
            }}
          >
            {subLabel}
          </span>
        ) : null}
      </div>
    </motion.div>
  );
}
